/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient.ts';
import type { ErrorType } from '../../../client/apiClient.ts';
import type { AccettazionePreventivoRequest } from '../schemas/accettazionePreventivoRequest.ts';
import type { PreventivoWithDettaglioDTO } from '../schemas/preventivoWithDettaglioDTO.ts';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce l’elenco di tutti i preventivi associati a una richiesta
 * @summary Recupero elenco preventivi associati ad una richiesta
 */
export const putFrontRichiesteIdPreventiviIdAccettazione = (
  id: string,
  idPreventivo: string,
  accettazionePreventivoRequest: AccettazionePreventivoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cmp-domande/richieste/${id}/preventivi/${idPreventivo}/accettazione`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: accettazionePreventivoRequest,
    },
    options,
  );
};

export const getPutFrontRichiesteIdPreventiviIdAccettazioneMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontRichiesteIdPreventiviIdAccettazione>>,
    TError,
    { id: string; idPreventivo: string; data: AccettazionePreventivoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putFrontRichiesteIdPreventiviIdAccettazione>>,
  TError,
  { id: string; idPreventivo: string; data: AccettazionePreventivoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putFrontRichiesteIdPreventiviIdAccettazione>>,
    { id: string; idPreventivo: string; data: AccettazionePreventivoRequest }
  > = props => {
    const { id, idPreventivo, data } = props ?? {};

    return putFrontRichiesteIdPreventiviIdAccettazione(
      id,
      idPreventivo,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PutFrontRichiesteIdPreventiviIdAccettazioneMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof putFrontRichiesteIdPreventiviIdAccettazione>>
  >;
export type PutFrontRichiesteIdPreventiviIdAccettazioneMutationBody =
  AccettazionePreventivoRequest;
export type PutFrontRichiesteIdPreventiviIdAccettazioneMutationError =
  ErrorType<unknown>;

/**
 * @summary Recupero elenco preventivi associati ad una richiesta
 */
export const usePutFrontRichiesteIdPreventiviIdAccettazione = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontRichiesteIdPreventiviIdAccettazione>>,
    TError,
    { id: string; idPreventivo: string; data: AccettazionePreventivoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutFrontRichiesteIdPreventiviIdAccettazioneMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce l’elenco di tutti i preventivi associati a una richiesta
 * @summary Recupero elenco preventivi associati ad una richiesta
 */
export const getFrontRichiesteIdPreventivi = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<PreventivoWithDettaglioDTO[]>(
    {
      url: `/framework-cmp-domande/richieste/${id}/preventivi`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdPreventiviQueryKey = (id: string) =>
  [`/framework-cmp-domande/richieste/${id}/preventivi`] as const;

export const getGetFrontRichiesteIdPreventiviQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdPreventivi>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdPreventivi>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdPreventivi>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontRichiesteIdPreventiviQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdPreventivi>>
  > = ({ signal }) => getFrontRichiesteIdPreventivi(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontRichiesteIdPreventiviQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdPreventivi>>
>;
export type GetFrontRichiesteIdPreventiviQueryError = ErrorType<unknown>;

/**
 * @summary Recupero elenco preventivi associati ad una richiesta
 */
export const useGetFrontRichiesteIdPreventivi = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdPreventivi>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdPreventivi>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdPreventiviQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce il pdf del preventivo associato alla richiesta
 * @summary Scarica il pdf del preventivo associato alla richiesta
 */
export const getFrontRichiesteIdPreventiviIdDownload = (
  id: string,
  idPreventivo: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Blob>(
    {
      url: `/framework-cmp-domande/richieste/${id}/preventivi/${idPreventivo}/download`,
      method: 'get',
      responseType: 'blob',
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdPreventiviIdDownloadQueryKey = (
  id: string,
  idPreventivo: string,
) =>
  [
    `/framework-cmp-domande/richieste/${id}/preventivi/${idPreventivo}/download`,
  ] as const;

export const getGetFrontRichiesteIdPreventiviIdDownloadQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdPreventiviIdDownload>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  idPreventivo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdPreventiviIdDownload>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdPreventiviIdDownload>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteIdPreventiviIdDownloadQueryKey(id, idPreventivo);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdPreventiviIdDownload>>
  > = ({ signal }) =>
    getFrontRichiesteIdPreventiviIdDownload(
      id,
      idPreventivo,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(id && idPreventivo),
    ...queryOptions,
  };
};

export type GetFrontRichiesteIdPreventiviIdDownloadQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdPreventiviIdDownload>>
>;
export type GetFrontRichiesteIdPreventiviIdDownloadQueryError =
  ErrorType<unknown>;

/**
 * @summary Scarica il pdf del preventivo associato alla richiesta
 */
export const useGetFrontRichiesteIdPreventiviIdDownload = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdPreventiviIdDownload>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  idPreventivo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdPreventiviIdDownload>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdPreventiviIdDownloadQueryOptions(
    id,
    idPreventivo,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
